import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Switch } from 'react-router-dom';
import { ConnectedRouter } from 'connected-react-router';
import cx from 'classnames';
import Loading from '@ifit/mp-loading';
import Reload from '@ifit/mp-reload';
import { I18nProvider } from '@ifit/rapport-webview';
import SearchHeader from '../../views/search/components/search-header';
import { ThemeContext } from '@ifit/themes/ThemeContext';
import { setThemeClass } from '@ifit/themes/setThemeClass';
import RestoreScroll from '../restore-scroll';
import routes from '../../views/routes';
import TrackedRoute from '../tracked-route';
import '@ifit/mary-poppins/dist/sass/_icon-font.scss';
import '../../styles/base.scss';
import { history } from '../../store';
import { useConfig } from '../../store/reducers/config';
import { useError } from '../../store/reducers/error';
import i18n from '../../i18n';

const App: React.FC = () => {
  const context = useContext(ThemeContext);
  const getPageHash = () => {
    const hash = window.location.hash.replace(/^#\//, '');
    return hash.split('?')[0].split('/')[0] || 'home';
  };
  const [pageHash, setPageHash] = useState(getPageHash);
  const { isAppConfigured, deviceType, language } = useConfig();
  const { errorText, displayError } = useError();
  const showLoading = !isAppConfigured;
  useEffect(() => {
    setThemeClass(context);
  }, [context]);
  const reload = useCallback(() => window.location.reload(), []);
  const routesMemo = useMemo(
    () => routes.map(r => <TrackedRoute key={r.path} {...r} />),
    [],
  );

  useEffect(() => {
    const onHashChange = () => {
      setPageHash(getPageHash());
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  return (
    <div className={cx('app', `is-${deviceType || 'tablet'} ${pageHash}-page`)}>
      {showLoading && <Loading key={'loading'} zIndex={11} />}
      {isAppConfigured && (
        <I18nProvider locale={language} instance={i18n}>
          <ConnectedRouter history={history}>
            <SearchHeader />
            {displayError && (
              <Reload key={'reload'} text={errorText} onReload={reload} />
            )}
            <RestoreScroll>
              <Switch>{routesMemo}</Switch>
            </RestoreScroll>
          </ConnectedRouter>
        </I18nProvider>
      )}
    </div>
  );
};

export default App;
